// Step 11 — Closing Confirmation (app.md §9). The queue of invoices at Pending
// Export, grouped by export batch: a set approved & exported together stays
// together here, and confirms together — all-or-nothing per batch (v1.24): no
// per-invoice Confirm, no selection; each batch header carries one green Confirm
// Batch button behind the "Are you sure?" dialog.
//
// Confirm is a decision per invoice (step 'Closing Confirmation Decision',
// 'approved') — the audit record. The API checks the role, advances each invoice
// to 'Bulk Imported Into Point Click Care' and closes the export batch out once
// its last invoice leaves Pending Export.
//
// PORT NOTE: the batch is one request now, each invoice its own transaction, and
// the response says per invoice whether it applied and why not. The old loop wrote
// the decisions one at a time and then waited on a background process, so a
// partial failure left half a batch "submitting" with a Retry that had to work out
// which rows to re-attempt.
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Subtitle2,
  Body1,
  Toast,
  ToastBody,
  ToastTitle,
  makeStyles,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { CheckmarkCircleRegular } from '@fluentui/react-icons'
import type { ExportBatch, Invoice } from '../lib/types'
import { DecisionOutcome, DecisionStep, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { useQueue } from '../lib/hooks'
import { createDecisionBatch, getExport, loadInvoicesByExportKey } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, type Session } from '../lib/identity'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { formatCurrency, formatDateTime } from '../lib/format'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner, WarningBanner } from '../components/States'
import { ScreenTitle } from '../components/ScreenTitle'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
  groups: { display: 'flex', flexDirection: 'column', gap: '28px' },
  groupHead: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  count: { color: tokens.colorNeutralForeground2 },
  spacer: { flexGrow: 1 },
  // The orange batch divider — same rule as Export Control's header/groups.
  groupRule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px' },
  // The same rule under the page header, matching Export Control's.
  headerRule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px', marginBottom: '16px' },
  // Overall sum across the batches; mirrors InvoiceQueue's per-group totals bar.
  grandTotal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
})

interface Props {
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called after confirms land, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ClosingConfirmationScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingExport], refreshNonce)
  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [bulkPending, setBulkPending] = useState<string[] | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices])

  // Authorization mirror (canDecide) — the API checks the Closing Confirmation
  // role for real; the button disables with the reason here.
  const authorized = invoices.length === 0 || canDecide(session, DecisionStep.ClosingConfirmation, invoices[0])
  const notAuthorizedReason =
    "You don't hold the Closing Confirmation role. You can view these invoices, but your confirmation would be refused."

  // The export batch behind each key — headers show who exported when. A missing
  // batch just falls back to the key-stub label.
  const [exportsByKey, setExportsByKey] = useState<Record<string, ExportBatch>>({})
  // Key on a stable, sorted join of the distinct export keys so a queue reload
  // (same rows, new array) doesn't refire this lookup.
  const exportKeyJoin = useMemo(
    () => [...new Set(invoices.map((i) => i.export_id).filter((k): k is string => !!k))].sort().join(','),
    [invoices],
  )
  useEffect(() => {
    const keys = exportKeyJoin ? exportKeyJoin.split(',') : []
    if (keys.length === 0) {
      setExportsByKey({})
      return
    }
    // allSettled, not all: one failed lookup must not drop every batch label.
    Promise.allSettled(keys.map((key) => getExport(key))).then((results) => {
      const next: Record<string, ExportBatch> = {}
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value) next[keys[i]] = r.value.export
      })
      setExportsByKey(next)
    })
  }, [exportKeyJoin])

  // One group per export batch, oldest export first; rows without a key collect
  // in a trailing group. Display grouping follows the filtered view; the batch
  // confirm always covers the FULL batch (all-or-nothing), so ids come from the
  // unfiltered queue.
  const batches = useMemo(() => {
    const byKey = new Map<string, Invoice[]>()
    for (const inv of filtered) {
      const key = inv.export_id ?? ''
      const rows = byKey.get(key)
      if (rows) rows.push(inv)
      else byKey.set(key, [inv])
    }
    return [...byKey.entries()]
      .map(([key, rows]) => ({
        key,
        invoices: rows,
        allIds: invoices.filter((i) => (i.export_id ?? '') === key).map((i) => i.id),
      }))
      .sort((a, b) => {
        if (!a.key) return 1
        if (!b.key) return -1
        return (exportsByKey[a.key]?.exported_at ?? '').localeCompare(exportsByKey[b.key]?.exported_at ?? '')
      })
  }, [filtered, invoices, exportsByKey])

  // Rows the page filter hides inside displayed batches. Worth a callout,
  // because Confirm Batch covers the FULL batch either way.
  const hiddenInBatches = batches.reduce((sum, b) => sum + (b.allIds.length - b.invoices.length), 0)

  function batchLabel(key: string): string {
    if (!key) return 'Not part of an export batch'
    const exp = exportsByKey[key]
    if (!exp) return `Export batch ${key.slice(0, 8)}`
    const who = exp.exported_by_name || exp.exported_by_email
    return `Exported ${formatDateTime(exp.exported_at)}${who ? ` by ${who}` : ''}`
  }

  // Re-read the batch from the server before confirming it. Confirm Batch means
  // the whole batch, and the queue behind an open dialog may have moved on — this
  // is the one place the full membership matters, so it is read rather than
  // inferred from what happens to be loaded.
  async function confirm(ids: string[], exportId: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      let batchIds = ids
      if (exportId) {
        const rows = await loadInvoicesByExportKey(exportId, InvoiceStatus.PendingExport)
        if (rows.length > 0) batchIds = rows.map((i) => i.id)
      }
      const result = await createDecisionBatch({
        step: DecisionStep.ClosingConfirmation,
        decision: DecisionOutcome.Approved,
        invoice_ids: batchIds,
      })
      setBulkPending(null)
      setOpenInvoice(null)
      void reload()
      onQueuesChanged?.()
      const refusals = result.results.filter((r) => !r.applied)
      if (refusals.length > 0) {
        const named = refusals
          .map((r) => {
            const inv = byId.get(r.invoice_id)
            const label = inv?.external_invoice_id || inv?.vendor_name || r.invoice_id
            return `${label}: ${r.error?.message ?? 'refused'}`
          })
          .join('; ')
        setActionError(
          `${result.applied_count} of ${result.results.length} confirmed. Not confirmed — ${named}`,
        )
      }
      const n = result.applied_count
      dispatchToast(
        <Toast>
          <ToastTitle>
            {n === 1
              ? 'Bulk imported into Point Click Care — invoice on its way to payment'
              : `Bulk imported into Point Click Care — ${n} invoices on their way to payment`}
          </ToastTitle>
          {refusals.length > 0 && <ToastBody>{refusals.length} stayed in the queue.</ToastBody>}
        </Toast>,
        { intent: refusals.length > 0 ? 'warning' : 'success' },
      )
    } catch (e) {
      setActionError(`Confirm failed: ${errorMessage(e)}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Closing Confirmation</ScreenTitle>
        <Body1 className={s.sub}>For each exported csv file, confirm that the import into Point Click Care was completed.</Body1>
      </div>
      <div className={s.headerRule} aria-hidden />

      <ErrorBanner message={error || actionError} />
      {/* Clears itself when the filters do (no dismiss). */}
      {hiddenInBatches > 0 && (
        <WarningBanner
          message={`The active filters hide ${hiddenInBatches} invoice${
            hiddenInBatches === 1 ? '' : 's'
          } inside the batches below. Confirm Batch always confirms a batch's full contents — hidden invoices included.`}
        />
      )}

      {loading ? (
        <Loading label="Loading invoices pending export…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : 'No invoices awaiting closing confirmation.'} />
      ) : (
        <div className={s.groups}>
          {batches.map((b, idx) => (
            <Fragment key={b.key || 'unbatched'}>
              {idx > 0 && <div className={s.groupRule} aria-hidden />}
              <section>
                <div className={s.groupHead}>
                  <Subtitle2>{batchLabel(b.key)}</Subtitle2>
                  <span className={s.count}>
                    {b.allIds.length} invoice{b.allIds.length === 1 ? '' : 's'}
                  </span>
                  <div className={s.spacer} />
                  <Button
                    size="small"
                    className={btn.approve}
                    icon={<CheckmarkCircleRegular />}
                    disabled={submitting || !authorized}
                    title={
                      authorized
                        ? 'Confirm the Point Click Care import completed for every invoice in this batch'
                        : notAuthorizedReason
                    }
                    onClick={() => setBulkPending(b.allIds)}
                  >
                    Bulk Imported Into Point Click Care
                  </Button>
                </div>
                <InvoiceQueue
                  invoices={b.invoices}
                  columns="amounts"
                  onOpen={setOpenInvoice}
                  totalsLabel="Batch total"
                />
              </section>
            </Fragment>
          ))}
          <div className={s.groupRule} aria-hidden />
          <div className={s.grandTotal}>
            <span>
              Grand total — {filtered.length} invoice{filtered.length === 1 ? '' : 's'}
            </span>
            <span>
              {formatCurrency(filtered.reduce((sum, i) => sum + (i.subtotal ?? 0), 0))} Subtotal +{' '}
              {formatCurrency(filtered.reduce((sum, i) => sum + (i.tax ?? 0), 0))} Tax ={' '}
              {formatCurrency(filtered.reduce((sum, i) => sum + (i.total ?? 0), 0))} Total
            </span>
          </div>
        </div>
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf
        user={user}
        onClose={() => setOpenInvoice(null)}
      />

      <DecisionDialog
        open={!!bulkPending}
        title="Are you sure?"
        body={
          bulkPending?.length === 1
            ? 'You are confirming that the Point Click Care import for this invoice is complete, meaning that it is on its way to payment. This will remove this invoice from the pipeline here. You cannot undo this action.'
            : 'You are confirming that the Point Click Care import for these invoices is complete, meaning that they are on their way to payment. This will remove these invoices from the pipeline here. You cannot undo this action.'
        }
        requireReason={false}
        confirmLabel="Bulk Imported Into Point Click Care"
        submitting={submitting}
        onCancel={() => setBulkPending(null)}
        onConfirm={() => {
          if (!bulkPending) return
          const batch = batches.find((b) => b.allIds === bulkPending)
          void confirm(bulkPending, batch?.key ?? '')
        }}
      />
    </div>
  )
}
